"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ResolveCancellationRequest } from "@chotca/sdk";
import type {
  AddMemberRequest,
  CreateOrgAddressRequest,
  UpdateOrgAddressRequest,
  AdminGiftCodeListQuery,
  AdminCreateGiftCodesRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useAdminDashboard() {
  return useQuery({
    queryKey: qk.admin.dashboard,
    queryFn: async () => getSdk().admin.dashboard(),
    staleTime: 30_000,
  });
}

type AdminAnalyticsRange = { from?: string; to?: string };

export function useAdminAnalyticsSummary(range: AdminAnalyticsRange = {}) {
  return useQuery({
    queryKey: qk.admin.analytics("summary", range),
    queryFn: async () => getSdk().admin.analyticsSummary(range),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminAnalyticsTimeseries(range: AdminAnalyticsRange = {}) {
  return useQuery({
    queryKey: qk.admin.analytics("timeseries", range),
    queryFn: async () => getSdk().admin.analyticsTimeseries(range),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminAnalyticsFunnel(range: AdminAnalyticsRange = {}) {
  return useQuery({
    queryKey: qk.admin.analytics("funnel", range),
    queryFn: async () => getSdk().admin.analyticsFunnel(range),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminAnalyticsRevenue(range: AdminAnalyticsRange = {}) {
  return useQuery({
    queryKey: qk.admin.analytics("revenue", range),
    queryFn: async () => getSdk().admin.analyticsRevenue(range),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminSubscriptionStats() {
  return useQuery({
    queryKey: ["admin", "subscriptionStats"],
    queryFn: async () => getSdk().admin.subscriptionStats(),
    staleTime: 30_000,
  });
}

// Orgs behind the "Pro từ mã / từ mua" dashboard stats. `source` is null while
// the (i) drill-down dialog is closed, so the query stays disabled until opened.
export function useProSourceOrgs(source: "GIFT" | "PURCHASE" | null) {
  return useQuery({
    queryKey: ["admin", "proSourceOrgs", source],
    queryFn: async () => getSdk().admin.proSourceOrgs(source!),
    enabled: !!source,
    staleTime: 30_000,
  });
}

export function useAdminUsers(
  query: {
    search?: string;
    status?: string;
    accountType?: string;
    globalRole?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.users(query),
    queryFn: async () =>
      getSdk().admin.listUsers(
        query as Parameters<ReturnType<typeof getSdk>["admin"]["listUsers"]>[0],
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminUserSessions(userId: string) {
  return useQuery({
    queryKey: qk.admin.userSessions(userId),
    queryFn: async () => getSdk().admin.listUserSessions(userId),
    staleTime: 15_000,
  });
}

export function useRevokeAdminUserSession(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      getSdk().admin.revokeUserSession(userId, sessionId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.userSessions(userId) }),
  });
}

export function useRevokeAllAdminUserSessions(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getSdk().admin.revokeAllUserSessions(userId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.userSessions(userId) }),
  });
}

export function useAdminWorkerKyc() {
  return useQuery({
    queryKey: qk.admin.workerKyc,
    queryFn: async () => getSdk().admin.listWorkerKyc(),
  });
}

export function useAdminOrgKyc() {
  return useQuery({
    queryKey: qk.admin.orgKyc,
    queryFn: async () => getSdk().admin.listOrgKyc(),
  });
}

export function useAdminOrgs(
  query: {
    search?: string;
    kycStatus?: string;
    status?: "active" | "deactivated";
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.orgs(query),
    queryFn: async () =>
      getSdk().admin.listOrgs(
        query as Parameters<ReturnType<typeof getSdk>["admin"]["listOrgs"]>[0],
      ),
    placeholderData: (prev) => prev,
  });
}

export function useDeactivateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => getSdk().admin.deactivateOrg(orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.admin.orgs() });
    },
  });
}

export function useActivateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => getSdk().admin.activateOrg(orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.admin.orgs() });
    },
  });
}

// Cross-org contacts list ("Liên hệ") — invites by lifecycle status, with the
// shared contact snapshot once an invite is committed.
export function useAdminInvites(
  query: {
    status?:
      | "LOCKED"
      | "ACCEPTED"
      | "COMMITTED"
      | "REJECTED"
      | "EXPIRED"
      | "CANCELLED";
    orgId?: string;
    from?: string;
    to?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.invites(query),
    queryFn: async () =>
      getSdk().admin.listInvites(
        query as Parameters<
          ReturnType<typeof getSdk>["admin"]["listInvites"]
        >[0],
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminCreditPurchases(
  query: {
    status?: "PENDING" | "PAID" | "EXPIRED" | "FAILED";
    orgId?: string;
    from?: string;
    to?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.creditPurchases(query),
    queryFn: async () =>
      getSdk().admin.listCreditPurchases(
        query as Parameters<
          ReturnType<typeof getSdk>["admin"]["listCreditPurchases"]
        >[0],
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

// -------- Plan gift codes ("mã quà tặng") --------
export function useAdminGiftCodes(query: AdminGiftCodeListQuery = {}) {
  return useQuery({
    queryKey: qk.admin.giftCodes(query),
    queryFn: async () => getSdk().admin.listGiftCodes(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useCreateGiftCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminCreateGiftCodesRequest) =>
      getSdk().admin.createGiftCodes(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.admin.giftCodes() });
    },
  });
}

export function useRevokeGiftCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().admin.revokeGiftCode(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.admin.giftCodes() });
    },
  });
}

export function useAdminCreditPurchaseSummary(
  query: {
    orgId?: string;
    from?: string;
    to?: string;
    search?: string;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.creditPurchaseSummary(query),
    queryFn: async () =>
      getSdk().admin.creditPurchaseSummary(
        query as Parameters<
          ReturnType<typeof getSdk>["admin"]["creditPurchaseSummary"]
        >[0],
      ),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminCreditPurchaseDetail(id: string | null) {
  return useQuery({
    queryKey: qk.admin.creditPurchase(id ?? ""),
    queryFn: async () => getSdk().admin.getCreditPurchase(id!),
    enabled: !!id,
  });
}

export function useAdminSyncCreditPurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().admin.syncCreditPurchase(id),
    onSuccess: (_res, id) => {
      void qc.invalidateQueries({ queryKey: ["admin", "creditPurchases"] });
      void qc.invalidateQueries({
        queryKey: ["admin", "creditPurchaseSummary"],
      });
      void qc.invalidateQueries({ queryKey: qk.admin.creditPurchase(id) });
    },
  });
}

export function useAdminMarkCreditPurchasePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; note: string }) =>
      getSdk().admin.markCreditPurchasePaid(input.id, { note: input.note }),
    onSuccess: (_res, input) => {
      void qc.invalidateQueries({ queryKey: ["admin", "creditPurchases"] });
      void qc.invalidateQueries({
        queryKey: ["admin", "creditPurchaseSummary"],
      });
      void qc.invalidateQueries({
        queryKey: qk.admin.creditPurchase(input.id),
      });
    },
  });
}

export function useAdminTodo() {
  return useQuery({
    queryKey: qk.admin.todo,
    queryFn: async () => getSdk().admin.todoCounts(),
    staleTime: 15_000,
  });
}

export function useAdminShadowOrgsWithApplicants(
  query: { search?: string; page?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: qk.admin.shadowOrgsWithApplicants(query),
    queryFn: async () => getSdk().admin.listShadowOrgsWithApplicants(query),
    placeholderData: (prev) => prev,
  });
}

// Unclaimed shadow orgs ("doanh nghiệp tạm") an admin can grant to an employer.
export function useAdminAssignableShadowOrgs(
  query: { search?: string; page?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: qk.admin.assignableShadowOrgs(query),
    queryFn: async () => getSdk().admin.listAssignableShadowOrgs(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

// Grant a shadow org to an employer account — it becomes the owner and the org
// stops being temporary. Refresh the account detail + the org/shadow lists.
export function useAdminGrantOrgToUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) =>
      getSdk().admin.grantOrgToUser(userId, { orgId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
      void qc.invalidateQueries({ queryKey: qk.admin.users() });
      void qc.invalidateQueries({ queryKey: qk.admin.orgs() });
      void qc.invalidateQueries({
        queryKey: qk.admin.assignableShadowOrgs(),
      });
      void qc.invalidateQueries({
        queryKey: qk.admin.shadowOrgsWithApplicants(),
      });
    },
  });
}

export function useAdminPostings(
  query: {
    state?: string;
    orgId?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.postings(query),
    queryFn: async () =>
      getSdk().admin.listPostings(
        query as {
          state?: undefined;
          orgId?: string;
          search?: string;
          page?: number;
          limit?: number;
        },
      ),
    placeholderData: (prev) => prev,
  });
}

export function useAdminApplicants(postingId: string) {
  return useQuery({
    queryKey: qk.admin.applicants(postingId),
    queryFn: async () => getSdk().admin.listApplicants(postingId),
    enabled: !!postingId,
  });
}

export function useAdminApplicantProfile(applicationId: string | null) {
  return useQuery({
    queryKey: qk.admin.applicantProfile(applicationId ?? ""),
    queryFn: async () =>
      getSdk().admin.getApplicantProfile(applicationId as string),
    enabled: !!applicationId,
  });
}

export function useAdminClaimInvitations(orgId: string) {
  return useQuery({
    queryKey: qk.admin.invitations(orgId),
    queryFn: async () => getSdk().admin.listClaimInvitations(orgId),
    enabled: !!orgId,
  });
}

export function useAdminAllClaimInvitations(
  query: {
    status?: "PENDING" | "CLAIMED" | "REVOKED" | "EXPIRED";
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.claimInvitations(query),
    queryFn: async () => getSdk().admin.listAllClaimInvitations(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminAbuseReports(
  query: {
    status?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.abuse(query),
    queryFn: async () => getSdk().admin.listAbuseReports(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminCancellationRequests(
  query: { status?: string; limit?: number } = {},
) {
  return useQuery({
    queryKey: ["admin", "cancellationRequests", query],
    queryFn: async () => getSdk().admin.listCancellationRequests(query),
    staleTime: 15_000,
  });
}

export function useAdminActivityLog(
  query: {
    actorUserId?: string;
    orgId?: string;
    action?: string;
    targetType?: string;
    targetId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: qk.admin.activity(query),
    queryFn: async () => getSdk().admin.listActivityLog(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

// Admin concierge view of an org's interview calendar ("Lịch phỏng vấn").
// Disabled until an orgId is known so detail pages can call it unconditionally.
export function useAdminOrgInterviews(
  orgId: string | null | undefined,
  query: { from?: string; to?: string; orgAddressId?: string } = {},
) {
  return useQuery({
    queryKey: qk.admin.orgInterviews(orgId ?? "", query),
    queryFn: async () => getSdk().admin.orgInterviews(orgId as string, query),
    enabled: !!orgId,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useResolveCancellationRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      requestId: string;
      body: ResolveCancellationRequest;
    }) =>
      getSdk().admin.resolveCancellationRequest(input.requestId, input.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cancellationRequests"] });
    },
  });
}

export function useAdminGrantCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { orgId: string; amount: number; reason: string }) =>
      getSdk().admin.grantCredits(input.orgId, input.amount, input.reason),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: qk.orgs.detail(vars.orgId) });
      qc.invalidateQueries({ queryKey: qk.admin.orgs() });
    },
  });
}

export function useAdminLogActAs() {
  return useMutation({
    mutationFn: (input: {
      orgId: string;
      action: string;
      metadata?: Record<string, unknown>;
    }) => getSdk().admin.logActAs(input.orgId, input.action, input.metadata),
  });
}

// -------- Admin: org branches (work locations) --------

export function useAdminOrgAddresses(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.admin.orgAddresses(orgId ?? ""),
    queryFn: async () => getSdk().admin.listOrgAddresses(orgId as string),
    enabled: !!orgId,
  });
}

export function useAdminCreateOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrgAddressRequest) =>
      getSdk().admin.createOrgAddress(orgId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.orgAddresses(orgId) }),
  });
}

export function useAdminUpdateOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrgAddressRequest }) =>
      getSdk().admin.updateOrgAddress(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.orgAddresses(orgId) }),
  });
}

export function useAdminArchiveOrgAddress(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().admin.archiveOrgAddress(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.orgAddresses(orgId) }),
  });
}

// -------- Admin: org members (staff) --------

export function useAdminOrgMembers(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.admin.orgMembers(orgId ?? ""),
    queryFn: async () => getSdk().admin.listOrgMembers(orgId as string),
    enabled: !!orgId,
    staleTime: 15_000,
  });
}

export function useAdminAddOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddMemberRequest) =>
      getSdk().admin.addOrgMember(orgId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.orgMembers(orgId) }),
  });
}

export function useAdminRemoveOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      getSdk().admin.removeOrgMember(orgId, membershipId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: qk.admin.orgMembers(orgId) }),
  });
}
